import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { getMyProfile } from "../services/platformApi";
import CTODOCXApp from "../apps/ctodocx/CTODOCXApp";
import "../styles/platform.css";
import logo from "../assets/images/cto_logo.png";
import gitlabLogo from "../assets/images/gitlab.webp";
import amazonQLogo from "../assets/images/amazon_Q.webp";
import plannerLogo from "../assets/images/planner_logo.webp";
import AdminOnboarding from "./admin/AdminOnboarding";
import ProfilePage from "./ProfilePage";
import ChangeRequests from "./admin/ChangeRequests";
import EmployeeManagement from "./admin/EmployeeManagement";

/**
 * PLATFORM SHELL
 * Collapsible sidebar + floating "My Profile" box + full-page app content.
 *
 * Nav model:
 *   Home       -> dashboard (My Apps box + Admin box + floating profile)
 *   My Apps    -> app launcher (CTODOCX box) -> opens CTODOCX full page
 *   My Profile -> full profile page (opened by clicking the floating box)
 *   Admin      -> admin dashboard (ADMIN role only)
 */

// ─── Inline SVG icon set (no icon fonts) ───
const Icon = {
  home: (
    <svg viewBox="0 0 24 24"><path d="M3 10.5L12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /></svg>
  ),
  apps: (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  ),
  doc: (
    <svg viewBox="0 0 24 24"><path d="M14 3H6v18h12V7z" /><path d="M14 3v4h4" /><path d="M9 13h6M9 17h6" /></svg>
  ),
  admin: (
    <svg viewBox="0 0 24 24"><circle cx="9" cy="8" r="3.5" /><path d="M3 20v-1a5 5 0 0 1 9-3" /><path d="M15 13l2 2 4-4" /></svg>
  ),
  shield: (
    <svg viewBox="0 0 24 24"><path d="M12 2l7 4v6c0 4-3 7-7 8-4-1-7-4-7-8V6z" /></svg>
  ),
  idcard: (
    <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18" /></svg>
  ),
  calendar: (
    <svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
  ),
  clock: (
    <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></svg>
  ),
  role: (
    <svg viewBox="0 0 24 24"><path d="M4 21v-1a6 6 0 0 1 12 0v1" /><circle cx="10" cy="7" r="4" /></svg>
  ),
  signout: (
    <svg viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>
  ),
  arrow: (
    <svg viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
  ),
  chevronLeft: (
    <svg viewBox="0 0 24 24"><path d="M15 6l-6 6 6 6" /></svg>
  ),
  chevronRight: (
    <svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg>
  ),
  close: (
    <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
  ),
};

function PlatformShell({ userRole }) {
  const { instance, accounts } = useMsal();
  const user = accounts[0];
  // Auto-detect if URL indicates CTODOCX should open directly
  // (handles /ctodocx path and ?share= parameter for share links)
  const [activeView, setActiveView] = useState(() => {
    const path = window.location.pathname.toLowerCase();
    const params = new URLSearchParams(window.location.search);
    const pendingShare = localStorage.getItem("ctodocx_pending_share");
    if (path.includes("ctodocx") || params.has("share") || pendingShare) {
      return "ctodocx";
    }
    return "home";
  });
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [imgModal, setImgModal] = useState(false);

  // ─── View Navigation with Browser History ───
  const navigateToView = (view) => {
    setActiveView(view);
    window.history.pushState({ pshellView: view }, "", "");
  };

  // Handle browser back/forward button for view navigation
  useEffect(() => {
    // Set initial history state
    window.history.replaceState({ pshellView: activeView }, "", "");

    const handlePopState = (event) => {
      // If this is a CTODOCX internal navigation state (folder nav),
      // let CTODOCXApp's own popstate handler deal with it — don't interfere.
      if (event.state && event.state.ctodocxPath !== undefined) {
        return;
      } else if (event.state && event.state.pshellView) {
        setActiveView(event.state.pshellView);
      } else {
        // No recognized state — go to home (don't exit app)
        setActiveView("home");
        window.history.replaceState({ pshellView: "home" }, "", "");
      }
      setMobileNavOpen(false);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);


  const isAdmin = userRole === "ADMIN";
  const initials = getInitials(user?.name || user?.username || "U");

  // Load own profile once (for the floating profile box)
  useEffect(() => {
    let alive = true;
    getMyProfile()
      .then((res) => {
        if (alive) setProfile(res && res.data ? res.data : res);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const handleLogout = () => {
    instance.logoutRedirect({
      account: user,
      postLogoutRedirectUri: window.location.origin,
    });
  };

  // ─── Sidebar nav items ───
  const menuItems = [
    { id: "home", name: "Home", icon: Icon.home },
    { id: "apps", name: "My Apps", icon: Icon.apps },
  ];
  const adminItems = isAdmin
    ? [{ id: "admin", name: "Admin", icon: Icon.admin, badge: 3 }]
    : [];

  const viewTitle =
    {
      home: "Home",
      apps: "My Apps",
      ctodocx: "CTODOCX",
      profile: "My Profile",
      admin: "Admin Panel",
    }[activeView] || "Home";

  const renderView = () => {
    switch (activeView) {
      case "ctodocx":
        return <CTODOCXApp user={user} />;
      case "apps":
        return <MyApps onOpen={() => navigateToView("ctodocx")} />;
      case "profile":
        return <ProfilePage />;
      case "admin":
        return <AdminPanel />;
      case "home":
      default:
        return (
          <HomePage
            firstName={(user?.name || "there").split(" ")[0]}
            isAdmin={isAdmin}
            onNavigate={navigateToView}
          />
        );
    }
  };

  return (
    <div className={`pshell ${collapsed ? "collapsed" : ""} ${mobileNavOpen ? "mobile-nav-open" : ""}`}>
      {/* ═══ SIDEBAR ═══ */}
      <aside className="pshell-sidebar">
        <div className="pshell-brand">
          <span className="pshell-logo">
            <img src={logo} alt="Cloud Techon" />
          </span>
          <span className="pshell-brand-txt">
            <span className="pshell-brand-name">CLOUD TECHON</span>

          </span>
        </div>

        {/* Expand / collapse toggle — anchored, always visible */}
        <button
          className="pshell-toggle"
          onClick={() => setCollapsed((c) => !c)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label="Toggle sidebar"
        >
          {collapsed ? Icon.chevronRight : Icon.chevronLeft}
        </button>

        <nav className="pshell-nav">
          <div className="pshell-nav-group">
            <div className="pshell-nav-title">Menu</div>
            {menuItems.map((it) => (
              <button
                key={it.id}
                className={`pshell-nav-item ${activeView === it.id ? "active" : ""}`}
                onClick={() => { navigateToView(it.id); setMobileNavOpen(false); }}
                title={it.name}
              >
                {it.icon}
                <span>{it.name}</span>
              </button>
            ))}
          </div>

          {adminItems.length > 0 && (
            <div className="pshell-nav-group">
              <div className="pshell-nav-title">Administration</div>
              {adminItems.map((it) => (
                <button
                  key={it.id}
                  className={`pshell-nav-item ${activeView === it.id ? "active" : ""}`}
                  onClick={() => { navigateToView(it.id); setMobileNavOpen(false); }}
                  title={it.name}
                >
                  {it.icon}
                  <span>{it.name}</span>
                  {it.badge ? <span className="pshell-nav-badge">{it.badge}</span> : null}
                </button>
              ))}
            </div>
          )}
        </nav>
      </aside>

      {/* Mobile backdrop — tap to close the drawer */}
      {mobileNavOpen && (
        <div
          className="pshell-backdrop"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {/* ═══ MAIN ═══ */}
      <div className="pshell-main">
        <header className="pshell-topbar">
          <button
            className="pshell-hamburger"
            aria-label="Open menu"
            onClick={() => setMobileNavOpen(true)}
          >
            <svg viewBox="0 0 24 24" width="22" height="22"><path d="M3 6h18M3 12h18M3 18h18" /></svg>
          </button>
          <div className="pshell-topbar-title">{viewTitle}</div>
        </header>

        <main className={`pshell-content${activeView === "ctodocx" ? " pshell-content--full" : ""}`}>
          {/* Floating My Profile box — only on Home */}
          {activeView === "home" && (
            <ProfileBox
              user={user}
              profile={profile}
              initials={initials}
              isAdmin={isAdmin}
              onOpenProfile={() => navigateToView("profile")}
              onOpenImage={() => setImgModal(true)}
              onLogout={handleLogout}
            />
          )}
          {renderView()}
        </main>
      </div>

      {/* ═══ IMAGE MODAL ═══ */}
      {imgModal && (
        <ImageModal
          src={profile?.photoUrl}
          initials={initials}
          name={profile?.fullName || user?.name}
          onClose={() => setImgModal(false)}
        />
      )}
    </div>
  );
}

// ─── Floating "My Profile" box ───
function ProfileBox({ user, profile, initials, isAdmin, onOpenProfile, onOpenImage, onLogout }) {
  const p = profile || {};
  const name = p.fullName || user?.name || "User";
  const designation = titleCase(p.designation || "Employee");
  const empId = p.employeeId || "—";
  const joining = p.dateOfJoining || p.dateOfjoining || null;
  const photo = p.photoUrl || null;

  // Clicking the card opens the profile page; specific controls stopPropagation.
  const openProfile = () => onOpenProfile && onOpenProfile();

  return (
    <div className="pshell-profile-wrap">
      <div className="pshell-profile-label">My Profile</div>
      <div
        className="pshell-profile-card"
        onClick={openProfile}
        role="button"
        tabIndex={0}
        title="Open my profile"
      >
        <div className="pshell-profile-body">
          <button
            className="pshell-profile-img"
            onClick={(e) => {
              e.stopPropagation();
              onOpenImage && onOpenImage();
            }}
            title="View photo"
          >
            {photo ? <img src={photo} alt={name} /> : <span>{initials}</span>}
          </button>

          <div className="pshell-profile-name">{name}</div>
          <div className="pshell-profile-badges">
            <span className="pshell-profile-role">
              {Icon.shield} {designation}
            </span>
            {isAdmin && (
              <span className="pshell-profile-role pshell-profile-role--admin">
                {Icon.shield} Admin
              </span>
            )}
          </div>

          <div className="pshell-profile-meta">
            <div className="pshell-profile-meta-item">
              <div className="pshell-profile-meta-label">{Icon.idcard} Emp ID</div>
              <div className="pshell-profile-meta-val">{empId}</div>
            </div>
            <div className="pshell-profile-meta-item">
              <div className="pshell-profile-meta-label">{Icon.role} Designation</div>
              <div className="pshell-profile-meta-val">{designation}</div>
            </div>
            <div className="pshell-profile-meta-item">
              <div className="pshell-profile-meta-label">{Icon.calendar} Joined</div>
              <div className="pshell-profile-meta-val">{formatJoined(joining)}</div>
            </div>
            <div className="pshell-profile-meta-item">
              <div className="pshell-profile-meta-label">{Icon.clock} Tenure</div>
              <div className="pshell-profile-meta-val">{computeTenure(joining)}</div>
            </div>
          </div>

          <button
            className="pshell-profile-signout"
            onClick={(e) => {
              e.stopPropagation();
              onLogout && onLogout();
            }}
          >
            {Icon.signout} Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Image modal ───
function ImageModal({ src, initials, name, onClose }) {
  return (
    <div className="pshell-modal-overlay" onClick={onClose}>
      <div className="pshell-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pshell-modal-close" onClick={onClose} aria-label="Close">
          {Icon.close}
        </button>
        <div className="pshell-modal-img">
          {src ? <img src={src} alt={name} /> : <span>{initials}</span>}
        </div>
        {name && <div className="pshell-modal-name">{name}</div>}
      </div>
    </div>
  );
}

// ─── Home dashboard ───
function HomePage({ firstName, isAdmin, onNavigate }) {
  return (
    <>
      <div className="pshell-welcome">
        <h1>Welcome back, {firstName}</h1>
        <p>Select an application to get started</p>
      </div>

      <div className="pshell-home-grid">
        <div className="pshell-app-card" onClick={() => onNavigate("apps")}>
          <div className="pshell-app-arrow">{Icon.arrow}</div>
          <div className="pshell-app-ico">{Icon.apps}</div>
          <h3>My Apps</h3>
          <p>Access your applications — CTODOCX and more coming soon.</p>
        </div>

        {isAdmin && (
          <div className="pshell-app-card admin" onClick={() => onNavigate("admin")}>
            <span className="pshell-card-badge">3</span>
            <div className="pshell-app-arrow">{Icon.arrow}</div>
            <div className="pshell-app-ico">{Icon.admin}</div>
            <h3>Admin</h3>
            <p>Onboarding approvals, change requests &amp; employee management.</p>
          </div>
        )}
      </div>
    </>
  );
}

// ─── My Apps launcher ───
function MyApps({ onOpen }) {
  return (
    <>
      <div className="pshell-view-head">
        <h1>My Apps</h1>
        <p>Applications available to you</p>
      </div>
      <div className="pshell-apps-inner-grid">
        <div className="pshell-app-card" onClick={onOpen}>
          <div className="pshell-app-arrow">{Icon.arrow}</div>
          <div className="pshell-app-ico">{Icon.doc}</div>
          <h3>CTODOCX</h3>
          <p>Document management — upload, organize, share &amp; preview files.</p>
        </div>

        <a
          className="pshell-app-card"
          href="https://git.cloudtechon.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="pshell-app-arrow">{Icon.arrow}</div>
          <div className="pshell-app-ico pshell-app-ico--img">
            <img src={gitlabLogo} alt="GitLab" />
          </div>
          <h3>Git</h3>
          <p>Source code management — repositories, merge requests &amp; CI/CD.</p>
        </a>

        <a
          className="pshell-app-card"
          href="https://kp9dgu8c.chat.qbusiness.us-east-1.on.aws/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="pshell-app-arrow">{Icon.arrow}</div>
          <div className="pshell-app-ico pshell-app-ico--img">
            <img src={amazonQLogo} alt="Amazon Q" />
          </div>
          <h3>Amazon Q</h3>
          <p>AI assistant — ask questions &amp; get help across your workspace.</p>
        </a>

        <a
          className="pshell-app-card"
          href="https://planner.cloud.microsoft/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="pshell-app-arrow">{Icon.arrow}</div>
          <div className="pshell-app-ico pshell-app-ico--img">
            <img src={plannerLogo} alt="Microsoft Planner" />
          </div>
          <h3>Planner</h3>
          <p>Task management — plan projects, assign tasks &amp; track progress.</p>
        </a>
      </div>
    </>
  );
}

// ─── Admin Panel ───
function AdminPanel() {
  const [tab, setTab] = useState("onboarding");

  const adminTabs = [
    { id: "onboarding", name: "Onboarding Approvals" },
    { id: "change-requests", name: "Change Requests" },
    { id: "employees", name: "Employees" },
  ];

  return (
    <div className="admin-panel">
      <div className="admin-panel-header">
        <h1>Admin Dashboard</h1>
        <p>Manage onboarding, profile change requests, and employees</p>
      </div>

      <div className="admin-subtabs">
        {adminTabs.map((t) => (
          <button
            key={t.id}
            className={`admin-subtab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            <span>{t.name}</span>
          </button>
        ))}
      </div>

      <div className="admin-panel-body">
        {tab === "onboarding" && <AdminOnboarding />}
        {tab === "change-requests" && <ChangeRequests />}
        {tab === "employees" && <EmployeeManagement />}
      </div>
    </div>
  );
}

// ─── Helpers ───
function getInitials(name) {
  if (!name) return "U";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

function titleCase(s) {
  if (!s) return "Employee";
  return s
    .toString()
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatJoined(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function computeTenure(dateStr) {
  if (!dateStr) return "—";
  const start = new Date(dateStr);
  if (isNaN(start.getTime())) return "—";
  const now = new Date();
  let months =
    (now.getFullYear() - start.getFullYear()) * 12 +
    (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) months = 0;

  const y = Math.floor(months / 12);
  const m = months % 12;

  const yPart = y > 0 ? `${y} ${y === 1 ? "year" : "years"}` : "";
  const mPart = m > 0 ? `${m} ${m === 1 ? "month" : "months"}` : "";

  if (!yPart && !mPart) return "New";
  return [yPart, mPart].filter(Boolean).join(" ");
}

export default PlatformShell;
